import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { Suspense, lazy, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AppLayout } from '../../../../components/layouts/AppLayout'
import BrainiacsLoader from '../../../../components/atoms/BrainiacsLoader'
import { useClientProfile } from '../../../../hooks/useClientProfile'
import { useAuth } from '../../../../contexts/AuthContext'
import BuyCreditsModal from '../../../../components/modals/BuyCreditsModal'
import InvoicesModal from '../../../../components/modals/InvoicesModal'

import { lazyWithRetry } from '../../../../utils/lazyWithRetry'

// Lazy load the campaign creator module with retry logic for chunk load errors
const CampaignCreatorModule = lazyWithRetry(() =>
  import('../../../../components/modules/CampaignCreator').then((m) => ({ default: m.CampaignCreatorModule }))
)

export const Route = createFileRoute('/campaigns/create/form')({
  validateSearch: (search: Record<string, unknown>) => ({
    // Creator mode: guided wizard vs CSV upload.
    mode: search.mode === 'csv' ? ('csv' as const) : ('form' as const),
    // Two-step wizard; legacy step 3 links open Outreach.
    step: Number.isInteger(search.step) && (search.step as number) >= 1 && (search.step as number) <= 3
      ? Math.min(search.step as number, 2)
      : undefined,
  }),
  component: CampaignFormPage,
})

function CampaignFormPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentUser } = useAuth()
  const clientQuery = useClientProfile(currentUser?.email)
  const { mode, step } = Route.useSearch()

  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false)
  const [isInvoicesModalOpen, setIsInvoicesModalOpen] = useState(false)

  const handleClose = () => {
    navigate({ to: '/campaigns' })
  }

  const handleSuccess = () => {
    // Invalidate queries to refresh data
    queryClient.invalidateQueries()
  }

  return (
    <AppLayout
      currentView="create"
      campaignTab="form"
      onBuyCredits={() => setIsBuyCreditsModalOpen(true)}
      onViewInvoices={() => setIsInvoicesModalOpen(true)}
      aiCredits={clientQuery.data?.credits ?? 0}
      creditsLoading={clientQuery.isPending}
      clientInfo={clientQuery.data}
    >
      <Suspense
        fallback={
          <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-28">
            <BrainiacsLoader variant="section" message="Preparing campaign creator" />
          </div>
        }
      >
        <CampaignCreatorModule
          activeTab={mode}
          onChangeTab={(next) =>
            navigate({ to: '/campaigns/create/form', search: { mode: next }, replace: true })
          }
          initialStep={step}
          onStepChange={(next) =>
            navigate({ to: '/campaigns/create/form', search: { mode, step: next }, replace: true })
          }
          onClose={handleClose}
          onSuccess={handleSuccess}
          credits={clientQuery.data?.credits ?? 0}
          clientProfile={clientQuery.data}
        />
      </Suspense>

      {/* Buy Credits Modal */}
      <BuyCreditsModal
        isOpen={isBuyCreditsModalOpen}
        onClose={() => setIsBuyCreditsModalOpen(false)}
      />

      {/* Invoices Modal */}
      <InvoicesModal
        isOpen={isInvoicesModalOpen}
        onClose={() => setIsInvoicesModalOpen(false)}
      />
    </AppLayout>
  )
}
